import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { FocalImage } from "@/components/ui/focal-image";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ImageCropDialog } from "@/components/recipes/ImageCropDialog";
import { Crop, Loader2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";



interface RecipeImageEditDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly currentImageUrl: string | null;
  readonly title: string;
  readonly imageHint?: string | null;
  readonly imageFocalPointX?: number;
  readonly imageFocalPointY?: number;
  readonly imageZoom?: number;
  readonly onImageSelected: (file: File | null) => void;
  readonly onRemoveImage: () => void;
  readonly onSave: () => void;
  readonly onCancel: () => void;
  readonly onCropSave?: (focalPointX: number, focalPointY: number, zoom: number) => void;
  readonly imageFile: File | null;
  readonly imagePreviewUrl: string | null;
  readonly removeImage: boolean;
  readonly isSubmitting: boolean;
}

export function RecipeImageEditDialog({
  open,
  onOpenChange,
  currentImageUrl,
  title,
  imageHint,
  imageFocalPointX,
  imageFocalPointY,
  imageZoom,
  onImageSelected,
  onRemoveImage,
  onSave,
  onCancel,
  onCropSave,
  imageFile,
  imagePreviewUrl,
  removeImage,
  isSubmitting,
}: RecipeImageEditDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(imagePreviewUrl);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);

  useEffect(() => {
    if (imagePreviewUrl) {

      setPreviewUrl(imagePreviewUrl);
    } else if (!imageFile && currentImageUrl && !removeImage) {
      setPreviewUrl(currentImageUrl);
    } else {
      setPreviewUrl(null);
    }
  }, [imagePreviewUrl, imageFile, currentImageUrl, removeImage]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    onImageSelected(file);
  };

  const handleRemove = () => {
    onRemoveImage();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCancel = () => {
    onCancel();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onOpenChange(false);
  };

  const handleSave = () => {
    onSave();
    onOpenChange(false);
  };

  const displayImage = removeImage ? null : previewUrl;
  const cropImageUrl = displayImage || currentImageUrl;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Recipe Image</DialogTitle>
            <DialogDescription>
              Upload a new recipe image. JPG, PNG, or GIF, max 5MB.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex justify-center">
              <div className="relative aspect-video w-full max-w-md overflow-hidden rounded-lg border">
                {displayImage ? (
                  <FocalImage
                    src={displayImage}
                    alt={imageHint ?? title}
                    className="absolute inset-0 h-full w-full"
                    data-ai-hint={imageHint ?? "recipe"}
                    focalPointX={imageFocalPointX}
                    focalPointY={imageFocalPointY}
                    zoom={imageZoom}
                  />
                ) : (
                  <div className="flex h-full items-center justify-center bg-muted">
                    <span className="text-sm text-muted-foreground">No image</span>
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="recipe-image-upload">Choose Image</Label>
              <Input
                id="recipe-image-upload"
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                disabled={isSubmitting}
                ref={fileInputRef}
              />
              <p className="text-xs text-muted-foreground">Upload a JPG, PNG, or GIF up to 5 MB.</p>
            </div>
            {currentImageUrl && !removeImage && (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCropDialogOpen(true)}
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  <Crop className="mr-2 h-4 w-4" />
                  Adjust Position
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleRemove}
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  <X className="mr-2 h-4 w-4" />
                  Remove Image
                </Button>
              </div>
            )}
            {removeImage && (
              <div className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
                Image will be removed when you save.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSubmitting || (!imageFile && !removeImage && !currentImageUrl)}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {cropImageUrl && onCropSave && (
        <ImageCropDialog
          open={cropDialogOpen}
          onOpenChange={setCropDialogOpen}
          imageUrl={cropImageUrl}
          initialFocalPointX={imageFocalPointX}
          initialFocalPointY={imageFocalPointY}
          initialZoom={imageZoom}
          onSave={onCropSave}
        />
      )}
    </>
  );
}
